import React from 'react';
import { Link } from 'react-router-dom';
import GuideLayout from '../common/GuideLayout';
import GuideCta, { FreeNote } from '../common/GuideCta';
import RelatedGuides from '../common/RelatedGuides';
import { relatedTo } from '../common/rsvpCluster';
import { SITE_SIGNUP_URL } from '../signup/startIntent';
import shotInvitation from '../../../assets/rsvp-1-invitation.webp';
import { COMPARE, EXAMPLE, FAQ, LIMITS, READING_MINUTES, STEPS, TOC, UPDATED } from './content';

// Page « Google Form mariage » : le pas-à-pas Google Forms (avec un exemple),
// ses limites, le comparatif et l'alternative ; appels à créer sa page RSVP.
const GoogleFormGuidePage: React.FC = () => (
  <GuideLayout
    eyebrow="Guide · Invitations"
    title="Google Form pour le mariage : le pas-à-pas, et une alternative plus simple"
    updated={UPDATED}
    published="2026-10-15"
    readingMinutes={READING_MINUTES}
    lead="Un Google Form pour recueillir les réponses de vos invités ? C’est gratuit et rapide à créer. Voici comment le faire bien, avec un exemple de formulaire complet, puis ses limites, et une alternative gratuite pensée pour les mariages."
    topCta={
      <GuideCta
        to={SITE_SIGNUP_URL}
        title="Plus simple qu’un Google Form"
        text="Vos invités choisissent leur nom dans votre liste et répondent pour toute leur famille, événement par événement."
        label="Créer mon formulaire gratuit"
      />
    }
    toc={TOC}
    faq={FAQ}
  >
    <section id="pourquoi">
      <h2>Pourquoi Google Forms est tentant</h2>
      <p>
        Gratuit, connu de tous, et un formulaire se crée en quelques minutes : beaucoup de couples pensent d’abord à
        Google Forms pour les réponses à leur mariage. Pour un petit mariage, avec un seul moment et peu de familles,
        c’est une solution qui tient la route.
      </p>
    </section>

    <section id="creer">
      <h2>Créer un Google Form pour son mariage</h2>
      <ol className="mg-steps">
        {STEPS.map((s) => (
          <li key={s.title}>
            <h3>{s.title}</h3>
            <p>{s.text}</p>
          </li>
        ))}
      </ol>
      <p>
        Pour le QR code, choisissez un générateur de QR code <b>statique</b> : certains créent des codes qui expirent.
        Tout est expliqué dans notre guide du <Link to="/qr-code-faire-part-mariage">QR code sur le faire-part</Link>.
      </p>
    </section>

    <section id="exemple">
      <h2>Exemple de Google Form de mariage</h2>
      <p>Les questions à créer, et le type de question à choisir dans Google Forms :</p>
      <div className="mg-table-scroll">
        <table className="mg-table mg-compare">
          <thead>
            <tr>
              <th scope="col">Question</th>
              <th scope="col">Type de question</th>
            </tr>
          </thead>
          <tbody>
            {EXAMPLE.map(([q, type]) => (
              <tr key={q}>
                <th scope="row">{q}</th>
                <td>{type}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        Une question par événement : avec un brunch ou un dîner réservé à une partie des invités, il faudra soit un
        formulaire par groupe, soit faire confiance à chacun pour répondre seulement à ce qui le concerne. Plus de
        détails sur les questions à poser dans notre guide du{' '}
        <Link to="/formulaire-reponse-mariage">formulaire de réponse de mariage</Link>.
      </p>
    </section>

    <section id="limites">
      <h2>Les limites de Google Forms pour un mariage</h2>
      <div className="mg-grid">
        {LIMITS.map((l) => (
          <div key={l.title} className="mg-card">
            <h3>{l.title}</h3>
            <p>{l.text}</p>
          </div>
        ))}
      </div>
    </section>

    <section id="comparatif">
      <h2>Google Form ou page RSVP : le comparatif</h2>
      <div className="mg-table-scroll">
        <table className="mg-table mg-compare mg-compare-3">
          <thead>
            <tr>
              <th scope="col">
                <span className="sr-only">Critère</span>
              </th>
              <th scope="col">Google Form</th>
              <th scope="col">Page RSVP Please</th>
            </tr>
          </thead>
          <tbody>
            {COMPARE.map(([label, form, page]) => (
              <tr key={label}>
                <th scope="row">{label}</th>
                <td>{form}</td>
                <td>{page}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    <figure className="mg-cta-figure">
      <img
        src={shotInvitation}
        width={420}
        height={909}
        loading="lazy"
        decoding="async"
        alt="Page d’invitation RSVP Please, alternative à Google Forms pour les réponses au mariage"
      />
      <figcaption>
        <p className="mg-cta-title">L’alternative gratuite, pensée pour les mariages</p>
        <p>
          Une page d’invitation à vos prénoms, avec lien et QR code. Chaque invité choisit son nom, répond pour chaque
          événement et pour sa famille, et vos compteurs se mettent à jour tout seuls.
        </p>
        <Link to={SITE_SIGNUP_URL} className="mkt-btn primary">
          Créer ma page gratuite
        </Link>
        <FreeNote />
      </figcaption>
    </figure>

    <RelatedGuides items={relatedTo('google')} />
  </GuideLayout>
);

export default GoogleFormGuidePage;
